import { useMemo } from "react";
import { scopeThreadRef, scopedThreadKey } from "@cleo/client-runtime/environment";

import { useClientSettings } from "~/hooks/useSettings";
import { useNowMinute } from "~/hooks/useNowMinute";
import type { SessionNotificationRow } from "@cleo/contracts";
import { WORLD_CONVERSATION_DOT_ENVIRONMENT } from "~/agentStrip";
import type { RootSessionStatus, RootSessionStripThreadInput } from "~/rootSessionStrip";
import { useThreadShells } from "~/state/entities";
import { useCloudConversations, type CloudConversationSummary } from "./cloudConversations";
import { foldWorldRequests as foldRequests } from "@cleo/shared/worldRequestFold";
import {
  completionCursor,
  currentCompletionScope,
  useCompletionReadsStore,
} from "./threadCompletionReads";
import { useNeucleosWorldStore } from "./worldStore";

// W-1c87244d (one-backend plan step 3, increment 5): a conversation kept in the World has no thread on this Desk, so the
// agent strip, which reads the Desk's threads, never showed one ("Inbox, all clear" while Cleo worked in the World). Each
// of the person's conversations in the entered World now stands in the strip as a thread input of its own, by the Mac's
// own rules: working while her turn runs, needing the person while it waits on their decision, finished once a turn has
// ended after the person last read it (the same completion receipt a Mac thread's result writes, under this key), and
// settled by the same "auto-settle after N days" setting.

/** The strip's environment for a conversation kept in the World (it lives in no environment of this Desk). */
export const WORLD_CONVERSATION_ENVIRONMENT = WORLD_CONVERSATION_DOT_ENVIRONMENT;

/** The completion receipt key of a conversation kept in the World: the one the strip's own clear writes for its dot. */
export const worldConversationThreadKey = (threadId: string): string =>
  scopedThreadKey(scopeThreadRef(WORLD_CONVERSATION_ENVIRONMENT as never, threadId as never));

const DAY_MS = 24 * 60 * 60 * 1000;

// Finished news is what ended while this Desk was watching the World (the cleo-1 run of 20 Oct 08:45Z read "15 finished"
// at the start: every conversation of the last 3 days, read on Pocket or elsewhere, which this Desk holds no receipt
// for). The moment this Desk first lists a World's conversations is kept per World on this device (a view convenience),
// and a turn that ended before it is not news here. In memory when storage is unavailable.
const WATCH_KEY = "cleo:world-conversation-strip-since:v1";
const watchedSince = new Map<string, number>();
export function worldWatchedSince(worldKey: string, nowMs: number): number {
  const known = watchedSince.get(worldKey);
  if (known !== undefined) return known;
  let stored: Record<string, number> = {};
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(WATCH_KEY) ?? "{}");
    if (parsed && typeof parsed === "object") stored = parsed as Record<string, number>;
  } catch {
    stored = {};
  }
  const since =
    typeof stored[worldKey] === "number" && Number.isFinite(stored[worldKey])
      ? stored[worldKey]!
      : nowMs;
  watchedSince.set(worldKey, since);
  if (stored[worldKey] !== since) {
    try {
      localStorage.setItem(WATCH_KEY, JSON.stringify({ ...stored, [worldKey]: since }));
    } catch {
      /* kept in memory for this run */
    }
  }
  return since;
}

/**
 * Can 36: helpers fold into the conversation that asked (@cleo/shared/worldRequestFold, shared with Pocket's home list). On the
 * Desk a copy of an earlier Pocket conversation (closed; its original stands for it) and a conversation a Desk thread already
 * holds are left out. Pure; shared by the strip and the Mac's notifications.
 */
export function foldWorldRequests(
  conversations: Iterable<CloudConversationSummary>,
  threadSessions: ReadonlySet<string>,
): ReadonlyArray<{
  readonly conversation: CloudConversationSummary;
  readonly state: CloudConversationSummary["state"];
  readonly updatedAt: number;
}> {
  return foldRequests(
    conversations,
    (conversation) =>
      conversation.movedFrom !== undefined || threadSessions.has(conversation.sessionId),
  );
}

/**
 * W-b6b6e41b (Can 36: "tell me when work needs me or finishes while I am away … on my Mac"): the person's conversations
 * kept in the entered World as rows of the Desk's own notification rule (UP6, @cleo/contracts detectSessionNotifications),
 * one per conversation (helpers rolled up by foldWorldRequests): waiting on the person is an approval, a new end while idle
 * a completion. The rule seeds on first sight and never replays; only the setting the person turned on shows them. Pure.
 */
export function worldConversationNotificationRows(
  conversations: Iterable<CloudConversationSummary>,
  input: { readonly worldKey: string; readonly threadSessions: ReadonlySet<string> },
): ReadonlyArray<SessionNotificationRow> {
  return foldWorldRequests(conversations, input.threadSessions).map(
    ({ conversation, state, updatedAt }) => ({
      environmentId: WORLD_CONVERSATION_ENVIRONMENT,
      id: conversation.threadId,
      title: conversation.title,
      archivedAt: null,
      // One wait is one notice: its key is the conversation's place when it began waiting.
      hasPendingApprovals: state === "waiting",
      hasPendingUserInput: false,
      worldKey: input.worldKey,
      sessionId: conversation.sessionId,
      session: null,
      latestTurn:
        state === "idle" && conversation.lastSeq === 0
          ? null
          : state === "idle"
            ? {
                turnId: `world-${updatedAt}`,
                state: "completed",
                completedAt: new Date(updatedAt).toISOString(),
              }
            : {
                turnId:
                  state === "waiting" ? `world-wait-${conversation.lastSeq}` : "world-running",
                state: "running",
                completedAt: null,
              },
    }),
  );
}

/** The strip's inputs for the person's conversations kept in `worldKey`. Pure. */
export function worldConversationStripInputs(
  conversations: Iterable<CloudConversationSummary>,
  input: {
    readonly worldKey: string;
    /** World sessions a thread of this Desk already holds: those stand in the strip once, as that thread. */
    readonly threadSessions: ReadonlySet<string>;
    /** When the person last read each conversation's result, by its receipt key; undefined when never. */
    readonly readCursor: (threadKey: string) => string | undefined;
    readonly now: string;
    readonly autoSettleAfterDays: number | null;
    /** When this Desk began watching the World: a turn that ended before it is not news here. */
    readonly watchingSince: number;
  },
): ReadonlyArray<RootSessionStripThreadInput> {
  const now = Date.parse(input.now);
  const inputs: RootSessionStripThreadInput[] = [];
  for (const { conversation, state, updatedAt } of foldWorldRequests(
    conversations,
    input.threadSessions,
  )) {
    const finishedAt = new Date(updatedAt).toISOString();
    const quiet =
      state === "idle" &&
      input.autoSettleAfterDays !== null &&
      updatedAt < now - input.autoSettleAfterDays * DAY_MS;
    const read = input.readCursor(worldConversationThreadKey(conversation.threadId));
    const status: RootSessionStatus | null =
      state === "running"
        ? "working"
        : state === "waiting"
          ? "approval"
          : conversation.lastSeq > 0 &&
              updatedAt > input.watchingSince &&
              (read === undefined || Date.parse(read) < updatedAt)
            ? "done"
            : null;
    inputs.push({
      environmentId: WORLD_CONVERSATION_ENVIRONMENT,
      threadId: conversation.threadId,
      worldKey: input.worldKey,
      sessionId: conversation.sessionId,
      archivedAt: null,
      pinnedAt: null,
      snoozedUntil: null,
      settledAt: quiet ? input.now : null,
      parentThreadId: null,
      title: conversation.title,
      status,
      finishedAt: status === "done" ? finishedAt : null,
    });
  }
  return inputs;
}

/** The person's conversations kept in the entered World, as the agent strip's thread inputs (signed in only). */
export function useWorldConversationStripInputs(): ReadonlyArray<RootSessionStripThreadInput> {
  const worldKey = useNeucleosWorldStore((state) => (state.signedIn ? state.activeWorldKey : null));
  const conversations = useCloudConversations(worldKey);
  const shells = useThreadShells();
  const buckets = useCompletionReadsStore((state) => state.buckets);
  const autoSettleAfterDays = useClientSettings((s) => s.sidebarAutoSettleAfterDays);
  const nowMinute = useNowMinute();
  return useMemo(() => {
    if (worldKey === null) return [];
    const scope = currentCompletionScope(worldKey);
    return worldConversationStripInputs(conversations.values(), {
      worldKey,
      threadSessions: new Set(
        shells.flatMap((shell) =>
          shell.worldKey === worldKey && shell.sessionId ? [shell.sessionId] : [],
        ),
      ),
      readCursor: (threadKey) => completionCursor(buckets, scope, threadKey),
      now: `${nowMinute}:00.000Z`,
      autoSettleAfterDays,
      watchingSince: worldWatchedSince(worldKey, Date.now()),
    });
  }, [autoSettleAfterDays, buckets, conversations, nowMinute, shells, worldKey]);
}

/** W-b6b6e41b: the person's conversations kept in the entered World as the Desk's notification rows (signed in only). */
export function useWorldConversationNotificationRows(): ReadonlyArray<SessionNotificationRow> {
  const worldKey = useNeucleosWorldStore((state) => (state.signedIn ? state.activeWorldKey : null));
  const conversations = useCloudConversations(worldKey);
  const shells = useThreadShells();
  return useMemo(() => {
    if (worldKey === null) return [];
    return worldConversationNotificationRows(conversations.values(), {
      worldKey,
      threadSessions: new Set(
        shells.flatMap((shell) =>
          shell.worldKey === worldKey && shell.sessionId ? [shell.sessionId] : [],
        ),
      ),
    });
  }, [conversations, shells, worldKey]);
}
